import { Box, Typography } from "@mui/material";
import { FC, useMemo } from "react";

import { useKpisTargetsAndReportsQueries } from "hooks/queries/kpis";
import { TurbineRow } from "pages/Kpis/TurbineRow";
import { getTargetedAndOthers } from "pages/Kpis/utils/getTargetedAndOthers";
import ProgressIndicatorContainer from "components/ProgressIndicatorContainer";

interface Props {
  kpiId: number;
  siteId: number;
}

export const TurbineList: FC<Props> = ({ kpiId, siteId }): JSX.Element => {
  const results = useKpisTargetsAndReportsQueries({
    kpi_id: kpiId,
    site_id: siteId,
    enabled: !!kpiId,
    page: 1,
    size: 999_999
  });

  const {
    isLoading: isTargetsLoading,
    isError: isTargetsError,
    data: targets
  } = results[0];

  const {
    isLoading: isReportsLoading,
    isError: isReportsError,
    data: reports
  } = results[1];

  const {
    isLoading: isMeasurementsLoading,
    isError: isMeasurementsError,
    data: measurements
  } = results[2];

  const { targeted, others } = useMemo(() => {
    if (
      !isTargetsLoading &&
      !isTargetsError &&
      !isReportsLoading &&
      !isReportsError &&
      !isMeasurementsLoading &&
      !isMeasurementsError
    ) {
      return getTargetedAndOthers(
        targets?.results,
        reports?.results,
        measurements?.results
      );
    }
    return { targeted: [], others: [] };
  }, [
    isTargetsLoading,
    isTargetsError,
    targets?.results,
    isReportsLoading,
    isReportsError,
    reports?.results,
    isMeasurementsLoading,
    isMeasurementsError,
    measurements?.results
  ]);

  return (
    <ProgressIndicatorContainer
      loading={isTargetsLoading || isReportsLoading}
      error={isTargetsError || isReportsError}
      errorMessage="An error occured whilst trying to load your items"
    >
      <Box sx={{ pl: 2 }}>
        {targeted.length > 0 && (
          <Typography component="h6" fontWeight="bold">
            Targeted
          </Typography>
        )}
        {targeted.map(target => (
          <TurbineRow
            key={target.id}
            machineName={target.machine_name}
            kpiId={kpiId}
            targetId={target.id}
            siteId={siteId}
            hasReport={target.hasReport}
            hasOverride={target.hasOverride}
            hasMeasurements={target.hasMeasurements}
            override={target.override}
            report={target.report}
            measurements={target.measurements}
          />
        ))}

        {others.length > 0 && (
          <Typography component="h6" fontWeight="bold" marginTop={1.75}>
            Others Completed
          </Typography>
        )}
        {others.map(target => (
          <TurbineRow
            key={target.id}
            machineName={target.machine_name}
            kpiId={kpiId}
            targetId={target.id}
            siteId={siteId}
            hasReport={target.hasReport}
            hasOverride={target.hasOverride}
            hasMeasurements={target.hasMeasurements}
            override={target.override}
            report={target.report}
            measurements={target.measurements}
          />
        ))}
      </Box>
    </ProgressIndicatorContainer>
  );
};
